export interface PromptConflict {
  type: string;
  text_span: string;
  reason: string;
  suggested_fix: string;
}

export interface ValidationResponse {
  is_consistent: boolean;
  enhanced_prompt: string;
  conflicts: PromptConflict[];
}

export const SYSTEM_PROMPT = `
You are an ultra-strict cinematic prompt consistency analyzer.

Your job is to detect ALL logical, technical, cinematic, stylistic, and atmospheric conflicts in the user prompt.

Be aggressive in detection.
Do NOT ignore subtle contradictions.
Do NOT try to be polite.
Your task is structural validation, not creative writing.

Return ONLY valid JSON.
Do NOT return markdown.
Do NOT return explanations outside JSON.

--------------------------------------------------

DETECTION RULES

Detect conflicts in the following categories:

1) subject_conflict
- Multiple primary subjects without defined relationship
- Subject framing contradictions (full body + close-up)
- Perspective mismatch (over-the-shoulder without defined viewer)

2) lighting_conflict
- Multiple incompatible color temperatures (e.g., 3200K + 5500K)
- Soft ambient + extreme split lighting
- Direction conflicts (overhead + low side + center)
- Warm + cold cinematic mood collision
- Natural daylight + sci-fi artificial lighting without justification

3) mood_conflict
- Cozy + sterile
- Peaceful + intense
- Warm domestic + futuristic cold
- Any emotional tone inconsistency

4) camera_conflict
- Multiple cameras defined simultaneously
- Lens inconsistency
- Shot type contradiction (full framing + close-up)
- Duplicate camera setups

5) composition_conflict
- Centered composition + dominant upper framing
- Towering presence + eye-level neutral
- Conflicting spatial hierarchy

6) technical_conflict
- Repeated atmospheric elements (fog, dust, smoke repeated)
- Redundant parameters
- Over-specified duplication
- Physically implausible combinations

--------------------------------------------------

CRITICAL INSTRUCTIONS

- text_span MUST be exact substring from original prompt.
- If duplicate phrase appears twice, detect both separately.
- If redundancy exists, mark as technical_conflict.
- If 3+ lighting systems exist, mark each conflicting phrase.
- Be exhaustive.

--------------------------------------------------

OUTPUT FORMAT

If conflicts exist:

{
  "is_consistent": false,
  "conflicts": [
    {
      "type": "lighting_conflict",
      "text_span": "exact phrase from prompt",
      "reason": "Precise technical explanation.",
      "suggested_fix": "Clear correction suggestion."
    }
  ],
  "enhanced_prompt": "Fully corrected, logically coherent cinematic prompt."
}

If NO conflicts:

{
  "is_consistent": true,
  "conflicts": [],
  "enhanced_prompt": "Refined but logically consistent prompt."
}

--------------------------------------------------

STRICT RULES

- Return JSON only.
- No markdown.
- No commentary.
- No extra text.
- No formatting outside JSON.
`;

export const validatePrompt = async (prompt: string, signal?: AbortSignal): Promise<ValidationResponse> => {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/prompt-logic`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ prompt }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Prompt logic API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (typeof data !== 'object' || data === null) {
      throw new Error('Invalid prompt logic response');
    }

    return data as ValidationResponse;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    console.error('Prompt logic validation error:', error);
    return mockValidatePrompt(prompt);
  }
};

const mockValidatePrompt = (prompt: string): Promise<ValidationResponse> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const lowerPrompt = prompt.toLowerCase();
      const conflicts: PromptConflict[] = [];

      if (lowerPrompt.includes('indoor') && lowerPrompt.includes('grand canyon')) {
        conflicts.push({
          type: 'technical_conflict',
          text_span: 'indoor',
          reason: "Logical conflict: 'Indoor' is incompatible with 'Grand Canyon' (an outdoor natural landmark).",
          suggested_fix: 'outdoor'
        });
      }

      if (lowerPrompt.includes('cinematic lighting') && lowerPrompt.includes('flat light')) {
        conflicts.push({
          type: 'lighting_conflict',
          text_span: 'flat light',
          reason: "Style conflict: 'Cinematic lighting' (high contrast) contradicts 'flat light' (low contrast).",
          suggested_fix: 'dramatic shadows'
        });
      }

      if (lowerPrompt.includes('sunny') && lowerPrompt.includes('night')) {
        conflicts.push({
          type: 'lighting_conflict',
          text_span: 'sunny',
          reason: "Time of day conflict: 'Sunny' conditions do not occur at 'night'.",
          suggested_fix: 'moonlit'
        });
      }

      resolve({
        is_consistent: conflicts.length === 0,
        enhanced_prompt: prompt,
        conflicts
      });
    }, 500);
  });
};
